/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, MouseEvent, useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import {
	FC_ANCHOR,
	FC_DIRECTION,
	FC_POSITION,
	FC_SIZE,
	FloatingContainer,
	FloatingContainerHeader
} from "../FloatingContainer";
import { FadeInOut } from "../FadeInOut";
import { Backdrop } from "../Backdrop";

export interface IInspectorFloatingPanelProps {
	/** Element to align panel to */
	for: React.MutableRefObject<HTMLElement>;
	/** If the panel is opened */
	isOpened: boolean;
	/** Where to place panel relative to target element */
	anchor?: FC_ANCHOR;
	/** Custom class name */
	className?: string;
	/** Function to render panel content */
	render: () => JSX.Element;
	/** Close event */
	onClose?: () => void;
}

export const InspectorFloatingPanel: FC<IInspectorFloatingPanelProps> = ({
	for: target,
	isOpened,
	anchor,
	className,
	render,
	onClose
}) => {
	const preventClick = useCallback(
		(ev: MouseEvent) => {
			if (isOpened) {
				ev.stopPropagation();
			}
		},
		[ isOpened ]
	);

	return (
		<FadeInOut
			isVisible={isOpened}
			fadeOutDelay={250}
			render={(isVisible) => {
				const baseClass = BEM("edt-inspector-floating-panel", null, {
					"is-visible": isVisible
				});

				if (className) {
					baseClass.add(className);
				}

				return (
					<>
						<FloatingContainer
							parent={target}
							anchor={anchor || FC_ANCHOR.LEFT_CENTER}
							direction={FC_DIRECTION.VR}
							size={FC_SIZE.AUTO}
							position={FC_POSITION.AUTO}
							className={baseClass.toClassName()}
							addArrow={true}
							offset={-1}
							safePadding={0}
							updateInterval={200}
							onClose={onClose}
							allowBackdrop
						>
							<FloatingContainerHeader onClose={onClose} />
							<div
								className={baseClass.element("content").toClassName()}
								onClick={preventClick}
							>
								{render()}
							</div>
						</FloatingContainer>
					</>
				);
			}}
		/>
	);
};
